import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Navigate } from 'react-router-dom';
import AdminPortalLayout from './AdminPortalLayout';
import {
  AdminMe,
  fetchReferralsList, fetchReferralApplications, fetchRefundsList,
  approveReferralApplication, rejectReferralApplication,
} from '../lib/admin-portal-api';
import { FaDollarSign, FaExclamationTriangle, FaCheckCircle } from '../lib/icons';

interface Props { me: AdminMe }

type Tab = 'active' | 'applications' | 'refunds';

export default function AdminPortalReferrals({ me }: Props) {
  const [tab, setTab] = useState<Tab>('active');

  if (!me.isSuperAdmin) {
    return <Navigate to="/admin-portal" replace />;
  }

  return (
    <AdminPortalLayout me={me} active="referrals">
      <Helmet><title>Referrals — EatMeat Admin</title></Helmet>

      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">Referrals</h1>
          <p className="text-sm text-gray-500 mt-1">Active influencers, pending applications, refund reversals.</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-1 flex gap-1 overflow-x-auto">
          {([
            { id: 'active', label: 'Active' },
            { id: 'applications', label: 'Applications' },
            { id: 'refunds', label: 'Refunds' },
          ] as { id: Tab; label: string }[]).map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                tab === t.id ? 'bg-primary text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'
              }`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'active' && <ActivePanel />}
      {tab === 'applications' && <ApplicationsPanel />}
      {tab === 'refunds' && <RefundsPanel />}
    </AdminPortalLayout>
  );
}

/* ---------- Active influencers ---------- */
function ActivePanel() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchReferralsList()
      .then((res) => setRows(res.data || []))
      .catch((e) => setError(e?.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const totalEarned = rows.reduce((s, r) => s + (r.commission_earned || 0), 0);
  const totalPaid = rows.reduce((s, r) => s + (r.commission_paid || 0), 0);
  const totalPending = totalEarned - totalPaid;

  return (
    <>
      {error && (
        <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 inline-flex items-start gap-2 mb-4">
          <FaExclamationTriangle className="mt-0.5 text-red-500 shrink-0" /> {error}
        </div>
      )}

      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-gradient-to-br from-green-50 to-green-100/40 ring-1 ring-green-100 rounded-2xl p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Earned</div>
          <div className="text-2xl font-extrabold text-green-700 mt-2">USD {totalEarned.toFixed(2)}</div>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-blue-100/40 ring-1 ring-blue-100 rounded-2xl p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Paid out</div>
          <div className="text-2xl font-extrabold text-blue-700 mt-2">USD {totalPaid.toFixed(2)}</div>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-orange-100/40 ring-1 ring-orange-100 rounded-2xl p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Pending</div>
          <div className="text-2xl font-extrabold text-orange-700 mt-2">USD {Math.max(totalPending, 0).toFixed(2)}</div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center text-sm text-gray-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
          <FaDollarSign className="text-4xl mx-auto mb-3 text-gray-200" />
          <p className="text-sm text-gray-500">No influencers yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-4 py-3">Influencer</th>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Rate</th>
                <th className="px-4 py-3">Signups</th>
                <th className="px-4 py-3">Paid conv.</th>
                <th className="px-4 py-3 text-right">Earned</th>
                <th className="px-4 py-3 text-right">Pending</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => {
                const pending = Math.max((r.commission_earned || 0) - (r.commission_paid || 0), 0);
                return (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <Link to={`/admin-portal/referrals/${r.id}`} className="block">
                        <div className="font-semibold text-gray-800">{r.name}</div>
                        <div className="text-xs text-gray-500">{r.email}</div>
                      </Link>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-primary">{r.code}</td>
                    <td className="px-4 py-3 text-xs">{r.commission_rate}%</td>
                    <td className="px-4 py-3">{r.total_signups}</td>
                    <td className="px-4 py-3">{r.total_subscriptions}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-800">{r.commission_currency} {r.commission_earned.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-orange-700">{r.commission_currency} {pending.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ---------- Applications ---------- */
function ApplicationsPanel() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try { const res = await fetchReferralApplications(); setRows(res.data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onApprove = async (a: any) => {
    const rateStr = window.prompt(`Commission rate (%) for ${a.name}:`, String(a.commission_rate || 10));
    if (rateStr === null) return;
    const codeProp = window.prompt(`Referral code (leave blank to keep "${a.code || '—'}"):`, '');
    if (codeProp === null) return;
    const rate = parseFloat(rateStr);
    setBusy(a.id);
    try {
      await approveReferralApplication(a.id, {
        commission_rate: Number.isFinite(rate) ? rate : undefined,
        code: codeProp.trim() || undefined,
      });
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Approve failed');
    } finally { setBusy(null); }
  };

  const onReject = async (a: any) => {
    const reason = window.prompt(`Reject ${a.name}'s application. Reason:`);
    if (reason === null) return;
    setBusy(a.id);
    try { await rejectReferralApplication(a.id, reason); await load(); }
    catch (e: any) { alert(e?.response?.data?.error || 'Reject failed'); }
    finally { setBusy(null); }
  };

  if (loading) return <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center text-sm text-gray-400">Loading…</div>;
  if (rows.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
        <FaCheckCircle className="text-4xl mx-auto mb-3 text-green-300" />
        <p className="text-sm text-gray-500">No pending applications.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {rows.map((a) => (
        <div key={a.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-gray-800">{a.name}</span>
                {a.platform && <span className="text-[11px] uppercase font-bold text-gray-400">{a.platform}</span>}
                {a.followers && <span className="text-[11px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">👥 {a.followers}</span>}
                {a.country && <span className="text-[11px] text-gray-500">{a.country}</span>}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">{a.email}</p>
              {a.social_url && (
                <a href={a.social_url} target="_blank" rel="noreferrer" className="text-[11px] text-primary hover:underline break-all block mt-1">{a.social_url}</a>
              )}
              {a.application_message && (
                <p className="text-sm text-gray-700 mt-3 whitespace-pre-wrap bg-gray-50 rounded-lg p-3">{a.application_message}</p>
              )}
              <p className="text-[11px] text-gray-400 mt-2">Applied {new Date(a.applied_at).toLocaleString()}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button disabled={busy === a.id} onClick={() => onReject(a)}
                className="text-xs px-3 py-1.5 rounded-md bg-red-50 text-red-700 hover:bg-red-100 font-semibold disabled:opacity-50">
                Reject
              </button>
              <button disabled={busy === a.id} onClick={() => onApprove(a)}
                className="text-xs px-3 py-1.5 rounded-md bg-primary text-white hover:opacity-90 font-semibold disabled:opacity-50">
                Approve
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Refunds ---------- */
function RefundsPanel() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRefundsList()
      .then((res) => setRows(res.data || []))
      .finally(() => setLoading(false));
  }, []);

  const total = rows.reduce((s, r) => s + Math.abs(r.commission_amount || 0), 0);

  if (loading) return <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center text-sm text-gray-400">Loading…</div>;
  if (rows.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
        <FaCheckCircle className="text-4xl mx-auto mb-3 text-green-300" />
        <p className="text-sm text-gray-500">No refund reversals on record.</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-gradient-to-br from-red-50 to-red-100/40 ring-1 ring-red-100 rounded-2xl p-4 mb-4 inline-flex flex-col">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Reversed</div>
        <div className="text-2xl font-extrabold text-red-700 mt-1">USD {total.toFixed(2)}</div>
        <div className="text-[11px] text-gray-400 mt-1">{rows.length} event{rows.length === 1 ? '' : 's'}</div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500">
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3">Influencer</th>
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Store</th>
              <th className="px-4 py-3 text-right">Amount reversed</th>
              <th className="px-4 py-3 text-right">Commission lost</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-xs text-gray-500">{new Date(r.createdAt).toLocaleString()}</td>
                <td className="px-4 py-3">
                  {r.referral ? (
                    <Link to={`/admin-portal/referrals/${r.referral.id}`} className="block">
                      <div className="font-semibold text-gray-800">{r.referral.name}</div>
                      <div className="text-xs font-mono text-primary">{r.referral.code}</div>
                    </Link>
                  ) : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-4 py-3 text-xs">
                  {r.user ? (
                    <Link to={`/admin-portal/users/${r.user.id}`} className="text-gray-700 hover:underline">
                      {r.user.username || r.user.email}
                    </Link>
                  ) : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">{r.provider_store || '—'}</td>
                <td className="px-4 py-3 text-right font-semibold text-red-700">{r.currency} {Math.abs(r.amount).toFixed(2)}</td>
                <td className="px-4 py-3 text-right font-semibold text-red-700">{r.currency} {Math.abs(r.commission_amount).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
